import { apiClient } from "@/lib/api/http"

export interface CreateRfqPayload {
  buyerId: string
  type: "CLOSE" | "OPEN"
  queryMessage?: string
  // validUntil?: string
  quantity?: number | null
  measureUnit?: string
  country?: string
  address?: string
  moq?: string
  budget?: number
  currencyCode?: string
  paymentTerms?: string
  leadTime?: string
  estimatedTime?: string
  estimatedTimeUnit?: string
  modeOfTransportation?: string
  destinationCityId?: string
  preferredShipmentMode?: string
  shipmentType?: string
  originAirport?: any
  originPort?: any
  pickupPincode?: any
  targets: {
    companyIds?: string[]
    productId?: string
    productVariantId?: string
    quantity?: number | null
    externalSearchId?: string
    externalCompanyNames?: string[]
    externalSellerIds?: string[]
  }
}

export interface RfqResponse {
  id: string
  buyerId: string
  type: string
  queryMessage: string | null
  validUntil: string
  createdAt: string
  updatedAt: string
}

export interface CompanyRfqTargetInfo {
  id: string
  name: string
  legalName?: string
  description?: string
  tags?: string[]
  email?: string
  phoneNumber?: string
  website?: string
  logoUrl?: string | null
  city?: string
  state?: string
  country?: string
  verificationStatus?: string
  createdAt?: string
  updatedAt?: string
}

export interface ProductRfqTargetInfo {
  id: string
  companyId: string
  productName: string
  description?: string
  tags?: string[]
  productCode?: string
  productCategory?: string
  productSubCategory?: string
  createdAt?: string
  updatedAt?: string
}

export interface RfqTargetItem {
  id: string
  rfqId: string
  companyId: string | null
  productId: string | null
  productVariantId: string | null
  quantity: number | null
  externalCompanyNames?: string[]
  createdAt: string
  updatedAt: string
  company: CompanyRfqTargetInfo | null
  product: ProductRfqTargetInfo | null
  productVariant: any | null
}

export interface RfqItem {
  id: string
  buyerId: string
  status: string
  type: string
  queryMessage: string | null
  validUntil: string
  moq?: string | null
  budget?: string | null
  budgetCurrency?: string | null
  paymentTerms?: string | null
  estimatedTime?: string | null
  estimatedTimeUnit?: string | null
  quantityUnit?: string | null
  createdByUserId: string | null
  canSellerAccess: boolean
  createdAt: string
  updatedAt: string
  targets: RfqTargetItem[]
  preferredShipmentMode?: string | null
  modeOfTransportation?: string | null
  shipmentType?: string | null
}

interface Envelope<T> {
  success: boolean
  statusCode: number
  message: string
  data: T
}

export async function createRfq(
  payload: CreateRfqPayload,
  token: string
): Promise<RfqResponse> {
  const hasTargets = !!(
    (payload.targets?.companyIds && payload.targets.companyIds.length > 0) ||
    payload.targets?.productId ||
    payload.targets?.productVariantId ||
    (payload.targets?.externalCompanyNames && payload.targets.externalCompanyNames.length > 0) ||
    (payload.targets?.externalSellerIds && payload.targets.externalSellerIds.length > 0)
  )

  const finalPayload: CreateRfqPayload = {
    ...payload,
    type: hasTargets ? "CLOSE" : "OPEN",
  }

  const { data: envelope } = await apiClient.post<Envelope<RfqResponse>>(
    "/rfq/buyer-to-admin",
    finalPayload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export interface RfqFilterParams {
  status?: string
  queryMessage?: string
  preferredShipmentMode?: string
  shipmentType?: string
  type?: string
  fromDate?: string
  toDate?: string
  page?: number
  limit?: number
  buyerId?: string
  companyId?: string
}

export async function getRfqBuyerToAdmin(token: string, filters?: RfqFilterParams, signal?: AbortSignal): Promise<RfqItem[]> {
  const { data: envelope } = await apiClient.get<any>(
    "/rfq/buyer-to-admin",
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: filters,
      signal,
    }
  )

  const rfqs = envelope?.data || []
  const totalCount = envelope?.meta?.total ?? rfqs.length
  const totalPages = envelope?.meta?.totalPages ?? Math.ceil(totalCount / (filters?.limit || 10))

  Object.defineProperty(rfqs, "totalCount", {
    value: totalCount,
    enumerable: false,
    writable: true,
    configurable: true
  })

  Object.defineProperty(rfqs, "totalPages", {
    value: totalPages,
    enumerable: false,
    writable: true,
    configurable: true
  })

  return rfqs
}

export async function getRfqAdminToSeller(token: string, filters?: RfqFilterParams, signal?: AbortSignal): Promise<RfqItem[]> {
  const { data: envelope } = await apiClient.get<any>(
    "/rfq/admin-to-seller",
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: filters,
      signal,
    }
  )

  const rfqs = envelope?.data || []
  const totalCount = envelope?.meta?.total ?? rfqs.length
  const totalPages = envelope?.meta?.totalPages ?? Math.ceil(totalCount / (filters?.limit || 10))

  Object.defineProperty(rfqs, "totalCount", {
    value: totalCount,
    enumerable: false,
    writable: true,
    configurable: true
  })

  Object.defineProperty(rfqs, "totalPages", {
    value: totalPages,
    enumerable: false,
    writable: true,
    configurable: true
  })

  Object.defineProperty(rfqs, "meta", {
    value: envelope?.meta,
    enumerable: false,
    writable: true,
    configurable: true
  })

  return rfqs
}

export interface ForwardRfqPayload {
  buyerRfqId: string
  companyId: string
  status: string
  queryMessage: string
  validUntil?: string | null
  canSellerAccess: boolean
  country?: string | null
  address?: string | null
  budget?: number | null
  currencyCode?: string | null
  quantity?: number | null
  measureUnit?: string | null
  paymentTerms?: string | null
  leadTime?: string | null
  targets: {
    productId: string | null
    productVariantId: string | null
    quantity: number
  }[]
}

export async function forwardRfqToSeller(
  payload: ForwardRfqPayload,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    "/rfq/admin-to-seller",
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export async function getRfqBuyerToAdminById(id: string, token: string): Promise<any> {
  const { data: envelope } = await apiClient.get<Envelope<any>>(
    `/rfq/buyer-to-admin/${id}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export async function getRfqBuyerToAdminWithQuotations(id: string, token: string): Promise<any> {
  const { data: envelope } = await apiClient.get<Envelope<any>>(
    `/rfq/buyer-to-admin/${id}/with-quotations`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export async function getRfqAdminToSellerById(id: string, token: string): Promise<any> {
  const { data: envelope } = await apiClient.get<Envelope<any>>(
    `/rfq/admin-to-seller/${id}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export async function getRfqBuyerToAdminCount(token: string): Promise<{ rfqCount: number }> {
  const { data: envelope } = await apiClient.get<Envelope<{ rfqCount: number }>>(
    "/rfq/buyer-to-admin/count",
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export async function getRfqAdminToSellerCount(token: string): Promise<{ rfqCount: number }> {
  const { data: envelope } = await apiClient.get<Envelope<{ rfqCount: number }>>(
    "/rfq/admin-to-seller/count",
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export async function addCompanyToRfq(
  rfqId: string,
  companyId: string,
  token: string
): Promise<any> {
  const { data: envelope } = await apiClient.post<Envelope<any>>(
    `/rfq/buyer-to-admin/${rfqId}/company-target`,
    { companyId },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export interface Checkpoint {
  type: string
  status: string
  timestamp: string
  data: any
}

export interface RfqStatusUpdates {
  rfqStatus: string
  checkpoints: Checkpoint[]
}

export async function getRfqBuyerToAdminStatusUpdates(
  id: string,
  token: string
): Promise<RfqStatusUpdates> {
  const { data: envelope } = await apiClient.get<Envelope<{ statusUpdates: RfqStatusUpdates }>>(
    `/rfq/buyer-to-admin/${id}/status-updates`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data.statusUpdates
}

export async function cancelRfqBuyerToAdmin(id: string, token: string): Promise<any> {
  const { data: envelope } = await apiClient.patch<Envelope<any>>(
    `/rfq/buyer-to-admin/${id}/cancel`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}

export async function rejectRfqAdminToSeller(id: string, token: string): Promise<any> {
  const { data: envelope } = await apiClient.patch<Envelope<any>>(
    `/rfq/admin-to-seller/${id}/reject`,
    {},
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )
  return envelope.data
}
